import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, HandHeart } from "lucide-react";
import { api, mediaUrl, formatError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SEO } from "@/components/SEO";
import { PageHero, SectionTitle, Loading } from "@/components/shared";
import { Checkbox } from "@/components/ui/checkbox";

export default function Sponsors() {
  const { settings } = useSettings();
  const [sponsors, setSponsors] = useState(null);
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", tier: "", message: "", consent: false });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/public/sponsors").then((r) => setSponsors(r.data.filter((s) => s.active))); }, []);

  const tiers = sponsors ? [...new Set(sponsors.map((s) => s.tier).filter(Boolean))] : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the privacy consent.");
    setSending(true);
    try {
      await api.post("/public/sponsor-enquiry", form);
      toast.success("Thank you! Your sponsorship enquiry has been sent.");
      setForm({ business_name: "", contact_name: "", email: "", phone: "", tier: "", message: "", consent: false });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const input = "w-full rounded-lg border border-slate-300 bg-white p-3 text-slate-900 placeholder:text-slate-400 focus:border-[#1E63D6] focus:outline-none focus:ring-2 focus:ring-[#1E63D6]/20";

  return (
    <>
      <SEO title="Sponsors | Brackenfell Junior Rugby Club" description="Our valued sponsors and partners. Become a BJRC sponsor and support youth rugby development in Brackenfell." />
      <PageHero title="Sponsors" subtitle="Our partners make junior rugby possible. Thank you for your support." />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {sponsors === null ? <Loading /> : sponsors.length === 0 ? (
          <p className="rounded-2xl bg-white p-10 text-center text-slate-500 ring-1 ring-slate-200">Our sponsor directory is being updated. Check back soon.</p>
        ) : tiers.length > 0 ? (
          tiers.map((tier) => (
            <div key={tier} className="mb-12">
              <SectionTitle eyebrow="Sponsorship Tier" title={tier} />
              <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
                {sponsors.filter((s) => s.tier === tier).map((s) => <SponsorCard key={s.id} s={s} />)}
              </div>
            </div>
          ))
        ) : (
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">{sponsors.map((s) => <SponsorCard key={s.id} s={s} />)}</div>
        )}
      </section>

      <section className="bg-[#0B2A4A] py-16">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="text-white">
            <HandHeart className="h-12 w-12 text-[#E8A317]" />
            <h2 className="mt-4 font-heading text-3xl font-black uppercase">Become a BJRC Sponsor</h2>
            <p className="mt-4 text-slate-300">{settings?.sponsorship_message}</p>
          </div>
          <form onSubmit={submit} className="rounded-2xl bg-white p-7 shadow-xl" data-testid="sponsor-enquiry-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required placeholder="Business name" className={input} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="sponsor-business-name" />
              <input required placeholder="Contact name" className={input} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="sponsor-contact-name" />
              <input required type="email" placeholder="Email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sponsor-email" />
              <input placeholder="Phone" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sponsor-phone" />
            </div>
            <textarea placeholder="Your message" rows={4} className={`${input} mt-4`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="sponsor-message" />
            <label className="mt-4 flex items-start gap-3 text-sm text-slate-600">
              <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="sponsor-consent" />
              <span>I consent to BJRC storing my details to respond to this enquiry (POPIA).</span>
            </label>
            <button disabled={sending} type="submit" data-testid="sponsor-inquiry-submit-button" className="mt-5 w-full rounded-lg bg-[#E8A317] py-3 font-extrabold uppercase text-[#0A1626] hover:bg-[#CC8F12] disabled:opacity-60">
              {sending ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

const SponsorCard = ({ s }) => (
  <a href={s.website || "#"} target={s.website ? "_blank" : undefined} rel="noreferrer" className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-6 text-center transition-all hover:shadow-md" data-testid={`sponsor-${s.id}`}>
    {s.logo_url ? <img src={mediaUrl(s.logo_url)} alt={s.name} className="max-h-20 max-w-full object-contain" /> : <Building2 className="h-12 w-12 text-slate-300" />}
    <span className="mt-3 text-sm font-bold text-[#0B2A4A]">{s.name}</span>
  </a>
);
